// "timeline" cluster: the staff-only chat action timeline in the workspace conversation view
// (src/features/workspace/components/conversations/TimelineRow.tsx) and the agent labels on message
// bubbles. Spread into the central LABELS in labels.ts.
//
// Sentences are built from entry type + params, never from the server's English `summary` (which carries raw
// ids). Hebrew sentences are phrased around the conversation or the noun so no verb has to agree with an actor
// whose gender is unknown ("השיחה נלקחה על ידי {name}", not a guessed "לקח/ה"). `{name}`, `{reason}` and
// `{n}` are interpolated by the workspace timeline helper - the portal's `t()` takes no variables.
export const TIMELINE_LABELS = {
  // Who an agent message bubble is from (OP-1041). "You" only for the signed-in user; a teammate by name; an
  // agent the directory does not list is a neutral "An agent" - never "You", never a raw id.
  tl_sender_you: { en: 'You', he: 'את/ה' },
  tl_sender_agent_unknown: { en: 'An agent', he: 'נציג' },

  tl_you: { en: 'you', he: 'את/ה' },
  tl_you_suffix: { en: '(you)', he: '(את/ה)' },
  tl_someone: { en: 'a teammate', he: 'חבר צוות' },
  tl_consult_requested: { en: 'Bot asked for help', he: 'הבוט ביקש עזרה' },
  tl_consulted_agent: { en: 'Bot consulted {name}', he: 'הבוט התייעץ עם {name}' },
  tl_consulted_team: { en: 'Bot consulted the team', he: 'הבוט התייעץ עם הצוות' },
  tl_guidance_sent: { en: '{name} answered the bot', he: 'תשובה לבוט מאת {name}' },
  tl_takeover: { en: '{name} took over', he: 'השיחה נלקחה על ידי {name}' },
  tl_takeover_reason: { en: '{name} took over — {reason}', he: 'השיחה נלקחה על ידי {name} — {reason}' },
  tl_release: { en: '{name} handed back to the bot', he: 'השיחה הוחזרה לבוט על ידי {name}' },
  tl_dismissed: { en: '{name} let the bot continue', he: 'הבוט הורשה להמשיך על ידי {name}' },
  tl_expired: { en: 'No teammate replied', he: 'אף חבר צוות לא הגיב' },
  tl_closed_customer: { en: 'The customer said it is solved; consultation closed', he: 'הלקוח אמר שזה נפתר; ההתייעצות נסגרה' },
  tl_sla: { en: 'No teammate responded in time', he: 'אף חבר צוות לא הגיב בזמן' },
  tl_sla_n: {
    en: 'No teammate responded within the service window ({n} min)',
    he: 'אף חבר צוות לא הגיב בזמן ({n} דק׳)',
  },
  tl_closed_by: { en: 'Closed by {name}', he: 'השיחה נסגרה על ידי {name}' },
  tl_closed_by_customer: { en: 'Closed by the customer', he: 'השיחה נסגרה על ידי הלקוח' },
  tl_closed_inactivity: { en: 'Closed automatically after inactivity', he: 'השיחה נסגרה אוטומטית עקב חוסר פעילות' },
  tl_closed_system: { en: 'Closed by the system', he: 'השיחה נסגרה על ידי המערכת' },
  tl_closed: { en: 'Conversation closed', he: 'השיחה נסגרה' },
  tl_spam: { en: 'Marked as spam by {name}', he: 'סומנה כספאם על ידי {name}' },
  tl_spam_unnamed: { en: 'Marked as spam', he: 'סומנה כספאם' },
  tl_spam_undone: { en: 'Spam mark undone by {name}', he: 'סימון הספאם בוטל על ידי {name}' },
  tl_spam_undone_unnamed: { en: 'Spam mark undone', he: 'סימון הספאם בוטל' },
  tl_draft_approved: {
    en: "{name} approved the bot's email reply",
    he: 'תשובת הבוט באימייל אושרה על ידי {name}',
  },
  tl_draft_edited: {
    en: "{name} edited and sent the bot's email reply",
    he: 'תשובת הבוט באימייל נערכה ונשלחה על ידי {name}',
  },
  tl_draft_auto_maxwait: {
    en: "The bot's email reply was sent automatically (review time ran out)",
    he: 'תשובת הבוט באימייל נשלחה אוטומטית (זמן הבדיקה הסתיים)',
  },
  tl_draft_auto_idle: {
    en: "The bot's email reply was sent automatically (nobody reviewed it)",
    he: 'תשובת הבוט באימייל נשלחה אוטומטית (איש לא בדק אותה)',
  },
  tl_draft_resolved: { en: 'Email draft resolved', he: 'טיוטת האימייל טופלה' },
  tl_unknown: { en: 'Conversation event', he: 'פעולה בשיחה' },
} satisfies Record<string, { en: string; he: string }>;
